


































































import { Component, Prop, Mixins } from "vue-property-decorator";
import RequestPromises from "@/mixins/requestPromises";
import HasValidation from "@/mixins/HasValidation";
import Context from "@/mixins/context";
import { DataModules } from "@/store/modules/data";
import { mapState } from "vuex";
import { IState } from "@/store";
import { SelectedPaymentMethod } from "@/models/selectPaymentMethod";
import { IClient } from "@/models/clients";
import { IBrand } from "@/models/brands";
import { IBrandGateway } from "@/models/gateways";
import { GatewayStoreType } from "@/data/enums/gateway";
import { GatewayTypes } from "@/data/enums";

@Component({
  components: {
    "brand-gateways": () =>
      import("@/components/app/global/payments/brandGateways/index.vue")
  },
  computed: {
    ...mapState({
      dataScope(state: IState, getters) {
        return getters[`data/${this.dataModule}/clientScope`](this.clientId);
      },
      gateways(state, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.BRAND_GATEWAYS
        });
      }
    })
  }
})
export default class AddPaymentMethodModal extends Mixins(
  RequestPromises,
  HasValidation,
  Context
) {
  // Props

  @Prop({ required: true })
  public client!: IClient;

  @Prop({ required: true })
  public clientId!: IClient["id"];

  @Prop({ required: true })
  public brandId!: IBrand["id"];

  // Data
  public isProcessing = false;
  public paymentDetails: SelectedPaymentMethod | null = null;
  public dataModule: string = DataModules.CLIENTS_PAYMENT_DETAILS;
  public dataScope!: string;
  public gateways!: IBrandGateway[];
  public hasUnpaidInvoices = false;
  public isLoading = true;

  // Life-cycle methods

  public async created() {
    try {
      await Promise.all([
        this.getBrandGateways(),
        this.$store
          .dispatch(`data/${DataModules.INVOICES}/hasUnpaid`, {
            clientId: this.clientId
          })
          .then(value => (this.hasUnpaidInvoices = value))
      ]);
    } finally {
      this.isLoading = false;
    }
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binList", {
      storeModule: DataModules.BRAND_GATEWAYS,
      vm: this
    });
  }

  // Computed
  public get unpaidInvoicesRoute() {
    return this.isAdmin
      ? {
          name: "adminClientInvoicesUnpaid",
          params: { clientId: this.clientId }
        }
      : {
          name: "clientInvoicesUnpaid"
        };
  }

  public get submitIsDisabled() {
    return (
      this.hasErrors ||
      this.isProcessing ||
      this.$_.isEmpty(this.paymentDetails)
    );
  }

  public get nonStoredProviders() {
    return this.gateways
      .filter(brandGateway => {
        // Check gateway cannot be stored
        const isNotStorable =
          brandGateway.gateway?.gateway_provider?.store_type ===
          GatewayStoreType.NONE;
        // Check gateway is available in account currency
        const isSupportedCurrency = brandGateway.gateway.currencies
          .map(c => c.currency_id)
          .includes(this.client?.accounts?.[0]?.currency_id);
        // Check gateway is not of type 'offline;
        const isNotOffline = ![GatewayTypes.OFFLINE].includes(
          brandGateway.gateway.type
        );
        return isNotStorable && isSupportedCurrency && isNotOffline;
      })
      .map(i => i.gateway.name);
  }

  // Methods

  public openAddManualPaymentModal({ gatewayId }) {
    this.$emit("open-manual-payment-modal", {
      storedOnly: true,
      clientId: this.clientId,
      brandId: this.brandId,
      gatewayId
    });
    this.$emit("close");
  }

  public getBrandGateways() {
    return this.$store.dispatch(`data/${DataModules.BRAND_GATEWAYS}/list`, {
      brandId: this.brandId,
      params: {
        limit: 0,
        order: "order",
        "filter[active]": 1,
        with: "gateway.gateway_provider,gateway.card_types"
      },
      vm: this
    });
  }

  public async validateBeforeSubmit() {
    if (this.submitIsDisabled) {
      return;
    }
    this.isProcessing = true;
    this.validate()
      .then(async result => {
        if (!result) {
          throw this.$t("_.form_invalid");
        }
        await this.$store.dispatch(`data/${this.dataModule}/create`, {
          scope: this.dataScope,
          id: this.clientId,
          data: this.$_.get(this.paymentDetails, "data")
        });
        this.$emit("payment-method-added");

        return this.$emit("close");
      })
      .catch(error => {
        this.$store.dispatch("api/handleValidationError", {
          error,
          vm: this
        });
      })
      .finally(() => {
        this.isProcessing = false;
      });
  }
}
